import { css } from 'aphrodite';
import { useMemo } from 'react';
import { Waypoint } from 'react-waypoint';
import * as Api from '../../../../../../extViewmodels';
import { useContactTimelineData } from '../../../../../components/entities/EntityTimeline/hooks/useTimelineData';
import { LocalNotificationObserver } from '../../../../../components/LocalNotificationObserver';
import { IAutomationInfo } from '../../../../../../models';
import { ILocalNotification } from '../../../../../../models/LocalNotifications';
import { Topics } from '../../../../../../models/LocalNotificationTopics';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { ContactTimelineList } from './list';
import { styleSheet } from './styles';

interface IProps {
	contactId: string;
	contactName: string;
	emailAddresses: Api.EmailAddress[];
	eventTypes: Api.TimelineEventTypes[];
	expandAllTimeline?: boolean;
	onDeleteRichContent: (richContent: Api.RichContentViewModel) => Promise<Api.IRichContent>;
	onMoreMenuOptionClicked?: (
		richContent: Api.RichContentViewModel,
		option: 'edit' | 'delete',
		e: React.MouseEvent<HTMLElement>
	) => void;
	setShouldStartPollingForRelationshipUpdate: (poll: boolean) => void;
}

export const ContactTimelineV2 = ({
	contactId,
	contactName,
	emailAddresses,
	eventTypes,
	expandAllTimeline,
	onDeleteRichContent,
	onMoreMenuOptionClicked,
	setShouldStartPollingForRelationshipUpdate,
}: IProps) => {
	const timelineData = useContactTimelineData({ contactId, eventTypes });

	const handleNoteCreatedOrEdited = (notification?: ILocalNotification<Api.INote>) => {
		if (timelineData.isLoading) return;
		const note = notification?.info;
		if (!note?.referencedEntities) return;
		const entityIds = [...note.referencedEntities.contacts, ...note.referencedEntities.companies].map(x => x.entity.id);
		setShouldStartPollingForRelationshipUpdate(true);
		if (entityIds.includes(contactId)) {
			timelineData.invalidateTimeline();
		}
	};

	const handleActionItemCreatedOrEdited = (notification?: ILocalNotification<Api.IActionItem>) => {
		if (timelineData.isLoading) return;
		const actionItem = notification?.info;
		if (!actionItem?.referencedEntities) return;
		const entityIds = [...actionItem.referencedEntities.contacts, ...actionItem.referencedEntities.companies].map(
			x => x.entity.id
		);
		if (entityIds.includes(contactId)) {
			timelineData.invalidateTimeline();
		}
	};

	const handleTimelineRefresh = () => {
		timelineData.invalidateTimeline();
	};

	const handleAutomationEdited = (notification?: ILocalNotification<IAutomationInfo>) => {
		if (
			contactId &&
			notification?.info?.contact?.id === contactId &&
			notification?.info?.automation?.steps?.some(x => x.step?._type === 'ActionItemAutomationStep')
		) {
			timelineData.invalidateTimeline();
		}
	};

	const timelinePages = useMemo(() => {
		if (!timelineData.data) {
			return [];
		}
		return timelineData.data.pages;
	}, [timelineData.data]);

	const timelineGroups = useMemo(() => {
		return timelinePages.map(group => {
			return group.values.filter(event => {
				// Sometimes the messages in conversation thread events are empty.
				// If an events last messages are all empty, skip the event in the timeline.
				if (!(event instanceof Date)) {
					const data = event.toJs();
					if (data._type === 'ConversationThreadEvent') {
						const conversationThreadEvent = data as Api.IConversationThreadEvent;
						if (!conversationThreadEvent.lastMessages?.some(msg => msg.text || msg.media)) {
							return false;
						}
					}
				}
				return true;
			});
		});
	}, [timelinePages]);

	return (
		<>
			<div className={css(styleSheet.timelineContainer)}>
				<ContactTimelineList
					items={timelineGroups}
					contactEmailAddresses={emailAddresses}
					contactName={contactName}
					expandAllTimeline={expandAllTimeline}
					onDeleteRichContent={onDeleteRichContent}
					onMoreMenuOptionClicked={onMoreMenuOptionClicked}
					onTimelineDataUpdated={() => timelineData.invalidateTimeline()}
				/>

				{(timelineData.isLoading || timelineData.isFetchingNextPage) && <LoadingSpinner />}

				{timelineData.hasNextPage && !timelineData.isFetchingNextPage ? (
					<Waypoint
						bottomOffset='-200px'
						onEnter={() => {
							timelineData.fetchNextPage();
						}}
					/>
				) : null}
			</div>
			<LocalNotificationObserver topic={Topics.CREATE_NOTE} onNotificationReceived={handleNoteCreatedOrEdited} />
			<LocalNotificationObserver topic={Topics.EDIT_NOTE} onNotificationReceived={handleNoteCreatedOrEdited} />
			<LocalNotificationObserver
				topic={Topics.CREATE_ACTION_ITEM}
				onNotificationReceived={handleActionItemCreatedOrEdited}
			/>
			<LocalNotificationObserver
				topic={Topics.EDIT_ACTION_ITEM}
				onNotificationReceived={handleActionItemCreatedOrEdited}
			/>
			<LocalNotificationObserver topic={Topics.TIMELINE_REFRESH} onNotificationReceived={handleTimelineRefresh} />
			<LocalNotificationObserver topic={Topics.CREATE_AUTOMATION} onNotificationReceived={handleAutomationEdited} />
			<LocalNotificationObserver topic={Topics.DELETE_AUTOMATION} onNotificationReceived={handleAutomationEdited} />
		</>
	);
};
